import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { Box, Flex, Grid, Heading, Text } from "@radix-ui/themes";
import styles from "./metal-braces.module.css";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import { useState, useRef, useEffect } from "react";

type Localization = {
  title: string;
  subtitle: string;
  introduction: string;
  // What Are Braces Made of Section
  materialsTitle: string;
  // Brackets
  bracketsTitle: string;
  bracketsContent: string;
  // Glue
  glueTitle: string;
  glueContent: string;
  // Wire
  wireTitle: string;
  wireContent: string;
  // Elastics
  elasticsTitle: string;
  elasticsContent: string;
  // Orthodontic Bands
  bandsTitle: string;
  bandsContent: string;
  // Spacers
  spacersTitle: string;
  spacersContent: string;
  // Elastic Ties
  tiesTitle: string;
  tiesContent: string;
  // FAQ Section
  faqTitle: string;
  faqItems: Array<{
    question: string;
    answer: string[];
  }>;
};

const englishLocalization: Localization = {
  title: "Metal Braces",
  subtitle: "Strong & Sturdy",
  introduction:
    "Traditional braces have been around for a long time, and they're probably the most recognizable symbol of orthodontics. These orthodontic workhorses have a long history of successful treatment and Dr. Garcia-Paul uses them to correct many dental issues.",

  materialsTitle: "What Are Braces Made of?",

  bracketsTitle: "Brackets",
  bracketsContent:
    "The brackets are durable and made from a mix of stainless steel, nickel, and other metals. Brackets have little hooks or doors where a wire is threaded. A bracket can be secured by closing the door, or by placing an elastic over the top of the wire.",

  glueTitle: "Glue",
  glueContent:
    "This is what Dr. Garcia-Paul will use to attach the brackets to your teeth. It's common to attach the bracket directly to the tooth with glue.",

  wireTitle: "Wire",
  wireContent:
    "This thin piece of metal runs from one bracket to another. Dr. Garcia-Paul will change the shape and curvature of the wire to move your teeth in the right direction. The wire will attach all of your bottom or upper teeth together in many cases, but we might occasionally cut the wire if connecting just a few teeth is better for your treatment plan.",

  elasticsTitle: "Elastics",
  elasticsContent:
    "If you need bite correction, elastics are essential. They are generally strung between an upper bracket hook and a lower bracket hook. This will pull the upper jaw backward to correct an overbite, or the lower jaw back to correct an underbite. We may use rubber bands for many different situations, especially when we want to put extra pressure on the teeth or jaws.",

  bandsTitle: "Orthodontic Bands",
  bandsContent:
    "These are stainless steel rings that are cemented to your teeth using dental bonding agents. These bands can provide an anchor for your braces and orthodontic appliances, but they are not used for everyone.",

  spacersTitle: "Spacers",
  spacersContent:
    'These small elastic "donuts" or rings can be used to create space in between your teeth when needed, typically before bands are placed. They are also referred to as separators.',

  tiesTitle: "Elastic Ties/O-Rings/Ligatures/Colors",
  tiesContent:
    "These tiny rubber rings or bands are used to attach the archwire to the brackets. They're less rigid than spacers and come in dozens of colors.",

  faqTitle: "Metal Braces FAQ",
  faqItems: [
    {
      question: "How long will I be in braces?",
      answer: [
        "This is what everyone wants to know, but it's one we don't have a concrete answer for. Every mouth is different, and every patient responds to treatment in their own way. Treatment time with metal braces will be different every time.",
        "The average time spent in metal braces is around 18 to 22 months, but could be shorter or longer, depending on the individual. We have patients in braces for as little as six months. But for complex issues, it can take over three years to complete the process.",
        "With metal braces, you can see signs of improvement in just a short period of time, and that gives you confidence during the treatment process.",
      ],
    },
    {
      question: "How do braces work?",
      answer: [
        "Before applying the brackets, Dr. Garcia-Paul and our team will collect photos and X-rays of your mouth. Sometimes we will us our iTero Element scanner to scan each of your teeth and the layout of your gums and mouth. If necessary, this process takes 10-15 minutes and provides an extremely accurate 3D view of your mouth.",
        "After the X-Ray and digital scan, the information will be used to help create your customized treatment plan. This will include how each tooth needs to be moved in order to get it in the best possible position. Dr. Garcia-Paul will decide how to place the brackets based on this information. For example, if you have some teeth that need to be tilted, the placement of those brackets will be different than the placement of brackets for teeth that need to be turned.",
        "Once the brackets have been attached, Dr. Garcia-Paul will insert the wire. Bends in the wire will provide different types of pressure on different teeth. A bend in the wire is how she can create specific and precise tooth movements.",
        "For example, a bend can help a tooth that is twisted to turn and face the right way, or aligning one tooth that is too far forward with one that is too far back. This process of tooth movement is called remodeling, and it involves minor changes in the bone that surrounds the roots of your teeth.",
      ],
    },
    {
      question: "What can I expect from today's metal braces?",
      answer: [
        "Metal braces have had a reputation for being awkward and obvious, but because of modern orthodontics, you will benefit from versions that are smaller, less conspicuous, and more comfortable than ever before. Sleeker brackets and lighter wires improve the appearance and feel of these braces. This means you will have less irritation in your mouth.",
        "Traditional metal braces also come with a fun new twist without any extra cost - you can customize them with pops of color. The elastics that fit around your brackets come in a wide variety of colors that let you showcase your personality whenever you want. Pick your favorite color combination, the colors of your favorite sports team, even your school colors! We also offer silver, gray, white, black, and tooth-colored elastics for patients seeking a more subtle look.",
        "Braces are great at fixing complex tooth issues. They can work faster at closing gaps and aligning teeth than other treatments might. This can potentially shorten your treatment time. Metal braces are a cost-effective option because they don't require some of the more complex technology or expensive equipment that newer treatments do.",
        "The strength, durability, and affordability of traditional metal braces keep them the top treatment for orthodontic patients, year after year! Because metal braces get right down to work, you'll be able to see some signs of progress in a relatively short period of time. It may not seem like much, but sometimes seeing even a small improvement in your smile can give you an encouraging boost on your orthodontic journey.",
      ],
    },
  ],
};

const spanishLocalization: Localization = {
  title: "Brackets metálicos",
  subtitle: "Fuertes y resistentes",
  introduction:
    "Los brackets tradicionales han existido durante mucho tiempo y probablemente son el símbolo más reconocible de la ortodoncia. Estos caballos de batalla ortodónticos tienen una larga historia de tratamiento exitoso y la Dra. García-Paul los utiliza para corregir muchos problemas dentales.",

  materialsTitle: "¿De qué están hechos los brackets?",

  bracketsTitle: "Brackets",
  bracketsContent:
    "Los brackets son duraderos y están hechos de una mezcla de acero inoxidable, níquel y otros metales. Los brackets tienen pequeños ganchos o puertas por donde se enhebra un alambre. Un bracket puede asegurarse cerrando la puerta o colocando un elástico sobre la parte superior del alambre.",

  glueTitle: "Pegamento",
  glueContent:
    "Esto es lo que la Dra. García-Paul usará para unir los brackets a sus dientes. Es común unir el bracket directamente al diente con pegamento.",

  wireTitle: "Alambre",
  wireContent:
    "Esta delgada pieza de metal va de un bracket a otro. La Dra. García-Paul cambiará la forma y la curvatura del alambre para mover sus dientes en la dirección correcta. El alambre unirá todos sus dientes inferiores o superiores en muchos casos, pero ocasionalmente podríamos cortar el alambre si conectar solo algunos dientes es mejor para su plan de tratamiento.",

  elasticsTitle: "Elásticos",
  elasticsContent:
    "Si necesita corrección de mordida, los elásticos son esenciales. Generalmente se colocan entre un gancho de bracket superior y un gancho de bracket inferior. Esto tirará de la mandíbula superior hacia atrás para corregir una sobremordida, o de la mandíbula inferior hacia atrás para corregir una submordida. Podemos usar bandas elásticas para muchas situaciones diferentes, especialmente cuando queremos ejercer presión adicional sobre los dientes o las mandíbulas.",

  bandsTitle: "Bandas ortodónticas",
  bandsContent:
    "Estos son anillos de acero inoxidable que se cementan a sus dientes usando agentes de unión dental. Estas bandas pueden proporcionar un anclaje para sus brackets y aparatos ortodónticos, pero no se usan para todos.",

  spacersTitle: "Espaciadores",
  spacersContent:
    'Estas pequeñas "donas" o anillos elásticos se pueden usar para crear espacio entre sus dientes cuando sea necesario, generalmente antes de colocar las bandas. También se conocen como separadores.',

  tiesTitle: "Ligaduras elásticas/O-rings/ligaduras/colores",
  tiesContent:
    "Estos pequeños anillos o bandas de goma se utilizan para unir el arco a los brackets. Son menos rígidos que los espaciadores y vienen en docenas de colores.",

  faqTitle: "Preguntas frecuentes sobre brackets metálicos",
  faqItems: [
    {
      question: "¿Cuánto tiempo tendré brackets?",
      answer: [
        "Esto es lo que todos quieren saber, pero es una pregunta para la que no tenemos una respuesta concreta. Cada boca es diferente, y cada paciente responde al tratamiento a su manera. El tiempo de tratamiento con brackets metálicos será diferente cada vez.",
        "El tiempo promedio que se pasa con brackets metálicos es de alrededor de 18 a 22 meses, pero podría ser más corto o más largo, dependiendo del individuo. Tenemos pacientes con brackets durante tan solo seis meses. Pero para problemas complejos, puede tomar más de tres años completar el proceso.",
        "Con brackets metálicos, puede ver signos de mejora en un período de tiempo relativamente corto, y eso le da confianza durante el proceso de tratamiento.",
      ],
    },
    {
      question: "¿Cómo funcionan los brackets?",
      answer: [
        "Antes de aplicar los brackets, la Dra. García-Paul y nuestro equipo recopilarán fotos y radiografías de su boca. A veces usaremos nuestro escáner iTero Element para escanear cada uno de sus dientes y la disposición de sus encías y boca. Si es necesario, este proceso toma de 10 a 15 minutos y proporciona una vista 3D extremadamente precisa de su boca.",
        "Después de la radiografía y el escaneo digital, la información se utilizará para ayudar a crear su plan de tratamiento personalizado. Esto incluirá cómo debe moverse cada diente para obtener la mejor posición posible. La Dra. García-Paul decidirá cómo colocar los brackets según esta información. Por ejemplo, si tiene algunos dientes que necesitan inclinarse, la colocación de esos brackets será diferente a la colocación de brackets para dientes que necesitan girarse.",
        "Una vez que se han colocado los brackets, la Dra. García-Paul insertará el alambre. Las curvas en el alambre proporcionarán diferentes tipos de presión sobre diferentes dientes. Una curva en el alambre es cómo ella puede crear movimientos dentales específicos y precisos.",
        "Por ejemplo, una curva puede ayudar a que un diente torcido gire y mire en la dirección correcta, o alinear un diente que está demasiado adelante con uno que está demasiado atrás. Este proceso de movimiento dental se llama remodelación e implica cambios menores en el hueso que rodea las raíces de sus dientes.",
      ],
    },
    {
      question: "¿Qué puedo esperar de los brackets metálicos actuales?",
      answer: [
        "Los brackets metálicos han tenido la reputación de ser incómodos y obvios, pero gracias a la ortodoncia moderna, se beneficiará de versiones que son más pequeñas, menos conspicuas y más cómodas que nunca. Los brackets más elegantes y los alambres más ligeros mejoran la apariencia y la sensación de estos brackets. Esto significa que tendrá menos irritación en su boca.",
        "Los brackets metálicos tradicionales también vienen con un nuevo giro divertido sin costo adicional: puede personalizarlos con toques de color. Los elásticos que se ajustan alrededor de sus brackets vienen en una amplia variedad de colores que le permiten mostrar su personalidad cuando lo desee. ¡Elija su combinación de colores favorita, los colores de su equipo deportivo favorito, incluso los colores de su escuela! También ofrecemos elásticos plateados, grises, blancos, negros y del color de los dientes para pacientes que buscan un aspecto más sutil.",
        "Los brackets son excelentes para solucionar problemas dentales complejos. Pueden funcionar más rápido para cerrar espacios y alinear los dientes que otros tratamientos. Esto puede potencialmente acortar su tiempo de tratamiento. Los brackets metálicos son una opción rentable porque no requieren tecnología más compleja o equipos costosos que los tratamientos más nuevos.",
        "¡La resistencia, durabilidad y asequibilidad de los brackets metálicos tradicionales los mantienen como el tratamiento principal para pacientes de ortodoncia, año tras año! Debido a que los brackets metálicos se ponen a trabajar de inmediato, podrá ver algunos signos de progreso en un período de tiempo relativamente corto. Puede que no parezca mucho, pero a veces ver incluso una pequeña mejora en su sonrisa puede darle un impulso alentador en su viaje ortodóntico.",
      ],
    },
  ],
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

export function MetalBracesPage() {
  const getLocalization = useLocalization(localized);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const answerRefs = useRef<(HTMLDivElement | null)[]>([]);

  const toggleFaq = (index: number) => {
    if (expandedFaq === index) {
      setExpandedFaq(null);
    } else {
      setExpandedFaq(index);
    }
  };

  // Update the height of the answer element when expanded
  useEffect(() => {
    answerRefs.current.forEach((ref, index) => {
      if (ref) {
        if (index === expandedFaq) {
          ref.style.height = `${ref.scrollHeight}px`;
        } else {
          ref.style.height = "0px";
        }
      }
    });
  }, [expandedFaq]);

  return (
    <>
      <HeroStyle2
        heroImageUrl="/metal-braces.jpg"
        title={getLocalization("title")}
        backgroundPosition="top"
      />

      <Box className={styles.introSection}>
        <Box className={styles.introContainer}>
          <Flex direction="column" gap="6" align="center">
            <Heading as="h2" className={styles.introHeading}>
              {getLocalization("subtitle")}
            </Heading>
            <Text size="5" align="center" className={styles.introText}>
              {getLocalization("introduction")}
            </Text>
          </Flex>
        </Box>
      </Box>

      <Flex
        className={`section ${styles.mainContentSection}`}
        width="100%"
        align="center"
        justify="center"
      >
        <Flex
          direction="column"
          className={styles.contentSection}
          gap="8"
          p="5"
        >
          {/* What Are Braces Made of Section */}
          <Flex direction="column" gap="6">
            <Heading as="h3" className={styles.subsectionHeading}>
              {getLocalization("materialsTitle")}
            </Heading>

            {/* Row 1: Brackets and Glue */}
            <Grid columns="12" gap="6">
              {/* Brackets - Left Column */}
              <Flex
                direction="column"
                gap="2"
                gridColumn={{ initial: "1 / 13", sm: "1 / 7" }}
              >
                <Heading as="h4" className={styles.subheading}>
                  {getLocalization("bracketsTitle")}
                </Heading>
                <Text size="5">{getLocalization("bracketsContent")}</Text>
              </Flex>

              {/* Glue - Right Column */}
              <Flex
                direction="column"
                gap="2"
                gridColumn={{ initial: "1 / 13", sm: "7 / 13" }}
              >
                <Heading as="h4" className={styles.subheading}>
                  {getLocalization("glueTitle")}
                </Heading>
                <Text size="5">{getLocalization("glueContent")}</Text>
              </Flex>
            </Grid>

            {/* Row 2: Wire and Elastics */}
            <Grid columns="12" gap="6">
              {/* Wire - Left Column */}
              <Flex
                direction="column"
                gap="2"
                gridColumn={{ initial: "1 / 13", sm: "1 / 7" }}
              >
                <Heading as="h4" className={styles.subheading}>
                  {getLocalization("wireTitle")}
                </Heading>
                <Text size="5">{getLocalization("wireContent")}</Text>
              </Flex>

              {/* Elastics - Right Column */}
              <Flex
                direction="column"
                gap="2"
                gridColumn={{ initial: "1 / 13", sm: "7 / 13" }}
              >
                <Heading as="h4" className={styles.subheading}>
                  {getLocalization("elasticsTitle")}
                </Heading>
                <Text size="5">{getLocalization("elasticsContent")}</Text>
              </Flex>
            </Grid>

            {/* Row 3: Orthodontic Bands and Spacers */}
            <Grid columns="12" gap="6">
              {/* Orthodontic Bands - Left Column */}
              <Flex
                direction="column"
                gap="2"
                gridColumn={{ initial: "1 / 13", sm: "1 / 7" }}
              >
                <Heading as="h4" className={styles.subheading}>
                  {getLocalization("bandsTitle")}
                </Heading>
                <Text size="5">{getLocalization("bandsContent")}</Text>
              </Flex>

              {/* Spacers - Right Column */}
              <Flex
                direction="column"
                gap="2"
                gridColumn={{ initial: "1 / 13", sm: "7 / 13" }}
              >
                <Heading as="h4" className={styles.subheading}>
                  {getLocalization("spacersTitle")}
                </Heading>
                <Text size="5">{getLocalization("spacersContent")}</Text>
              </Flex>
            </Grid>

            {/* Row 4: Elastic Ties (only on left) */}
            <Grid columns="12" gap="6">
              {/* Elastic Ties - Left Column */}
              <Flex
                direction="column"
                gap="2"
                gridColumn={{ initial: "1 / 13", sm: "1 / 7" }}
              >
                <Heading as="h4" className={styles.subheading}>
                  {getLocalization("tiesTitle")}
                </Heading>
                <Text size="5">{getLocalization("tiesContent")}</Text>
              </Flex>
            </Grid>
          </Flex>
        </Flex>
      </Flex>

      {/* FAQ Section with blue background */}
      <Box className={styles.faqSection}>
        <Box className={styles.faqContainer}>
          <Heading as="h2" className={styles.faqTitle}>
            {getLocalization("faqTitle")}
          </Heading>

          {getLocalization("faqItems").map((faq, index) => (
            <div key={faq.question} className={styles.faqItem}>
              <button
                type="button"
                className={styles.faqQuestion}
                onClick={() => toggleFaq(index)}
                aria-expanded={expandedFaq === index}
              >
                {faq.question}
                <span
                  className={`${styles.faqPlusIcon} ${
                    expandedFaq === index ? styles.faqPlusIconOpen : ""
                  }`}
                >
                  +
                </span>
              </button>
              <div
                ref={(el) => {
                  answerRefs.current[index] = el;
                }}
                className={`${styles.faqAnswer} ${
                  expandedFaq === index ? styles.faqAnswerOpen : ""
                }`}
              >
                <Flex direction="column" gap="4">
                  {faq.answer.map((paragraph) => (
                    <Text key={paragraph} size="5">
                      {paragraph}
                    </Text>
                  ))}
                </Flex>
              </div>
            </div>
          ))}
        </Box>
      </Box>

      <Box className={styles.form}>
        <HomeConsultForm />
      </Box>
    </>
  );
}
